import React, { useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  CampaignSummary,
  NewsletterAlign,
  NewsletterBlock,
  NewsletterImagePosition,
  NewsletterProductItem,
  NewsletterSocialLink,
  newBlockId,
} from "../../../model/NewsletterCampaign";
import { uploadNewsletterImage } from "../../../helpers/newsletterCampaignsApi";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";
const smallInputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";
const buttonSecondary =
  "inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50";

const BLOCK_LABELS: Record<string, string> = {
  header: "Cabeçalho",
  heading: "Título",
  text: "Texto",
  image: "Imagem",
  "image-text": "Imagem + Texto",
  button: "Botão",
  spacer: "Espaço",
  divider: "Separador",
  products: "Produtos",
  campaign: "Campanha",
  coupon: "Cupão",
  social: "Redes sociais",
  html: "HTML",
  footer: "Rodapé",
};

const ADDABLE = [
  "header",
  "heading",
  "text",
  "image",
  "image-text",
  "button",
  "products",
  "campaign",
  "coupon",
  "social",
  "spacer",
  "divider",
  "html",
  "footer",
];

const SOCIAL_NETWORKS = [
  "instagram",
  "facebook",
  "linkedin",
  "x",
  "youtube",
  "tiktok",
  "whatsapp",
  "website",
];

interface Props {
  blocks: NewsletterBlock[];
  onChange: (blocks: NewsletterBlock[]) => void;
  campaigns: CampaignSummary[];
}

const newBlock = (type: string): NewsletterBlock => {
  switch (type) {
    case "header":
      return { type, store_name: "", logo_url: "" };
    case "heading":
      return { type, text: "Título" };
    case "text":
      return { type, html: "<p>Escreve aqui o teu texto.</p>" };
    case "image":
      return { type, src: "", alt: "" };
    case "image-text":
      return {
        type,
        src: "",
        alt: "",
        image_position: "left",
        title: "",
        html: "<p>Escreve aqui o teu texto.</p>",
      };
    case "button":
      return { type, label: "Saber mais", href: "https://" };
    case "spacer":
      return { type, height: 24 };
    case "products":
      return { type, layout: "grid2", items: [] };
    case "campaign":
      return { type, campaign_id: "", layout: "grid2", items: [] };
    case "coupon":
      return {
        type,
        coupon_code: "BEMVINDO10",
        coupon_description: "10% de desconto",
        coupon_expires: "",
        coupon_cta_label: "Comprar agora",
        coupon_cta_href: "",
      };
    case "social":
      return { type, social: [{ network: "instagram", url: "" }] };
    case "html":
      return { type, html: "<p></p>" };
    case "footer":
      return {
        type,
        store_name: "Niposom",
        unsubscribe_label: "Cancelar subscrição",
      };
    default:
      return { type };
  }
};

const StyleFields: React.FC<{
  block: NewsletterBlock;
  onChange: (patch: Partial<NewsletterBlock>) => void;
}> = ({ block, onChange }) => (
  <div className="grid gap-2 sm:grid-cols-6">
    <label className="text-xs text-gray-600">
      Fundo
      <input
        className={smallInputClass}
        placeholder="#f9fafb"
        value={block.background || ""}
        onChange={(e) => onChange({ background: e.target.value })}
        aria-label="Cor de fundo"
      />
    </label>
    <label className="text-xs text-gray-600">
      Texto
      <input
        className={smallInputClass}
        placeholder="#111827"
        value={block.text_color || ""}
        onChange={(e) => onChange({ text_color: e.target.value })}
        aria-label="Cor do texto"
      />
    </label>
    <label className="text-xs text-gray-600">
      Alinhamento
      <select
        className={smallInputClass}
        value={block.align || ""}
        onChange={(e) =>
          onChange({ align: (e.target.value || undefined) as NewsletterAlign })
        }
        aria-label="Alinhamento"
      >
        <option value="">Padrão</option>
        <option value="left">Esquerda</option>
        <option value="center">Centro</option>
        <option value="right">Direita</option>
      </select>
    </label>
    <label className="text-xs text-gray-600">
      Padding (px)
      <input
        className={smallInputClass}
        type="number"
        min={0}
        max={64}
        value={block.padding ?? 0}
        onChange={(e) => onChange({ padding: Number(e.target.value) || 0 })}
        aria-label="Padding"
      />
    </label>
    <label className="text-xs text-gray-600">
      Margem (px)
      <input
        className={smallInputClass}
        type="number"
        min={0}
        max={64}
        value={block.margin ?? 0}
        onChange={(e) => onChange({ margin: Number(e.target.value) || 0 })}
        aria-label="Margem"
      />
    </label>
    <label className="text-xs text-gray-600">
      Raio (px)
      <input
        className={smallInputClass}
        type="number"
        min={0}
        max={24}
        value={block.radius ?? 0}
        onChange={(e) => onChange({ radius: Number(e.target.value) || 0 })}
        aria-label="Raio"
      />
    </label>
  </div>
);

const NewsletterBlockEditor: React.FC<Props> = ({
  blocks,
  onChange,
  campaigns,
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  const update = (index: number, patch: Partial<NewsletterBlock>) => {
    const next = blocks.map((block, i) =>
      i === index ? { ...block, ...patch } : block,
    );
    onChange(next);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const dropAt = (target: number) => {
    if (dragIndex === null || dragIndex === target) {
      setDragIndex(null);
      return;
    }
    const next = [...blocks];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(target, 0, moved);
    onChange(next);
    setDragIndex(null);
  };

  const remove = (index: number) => {
    if (!confirm("Remover este bloco?")) return;
    onChange(blocks.filter((_, i) => i !== index));
  };

  const importCampaignItems = (index: number, campaignId: string) => {
    const campaign = campaigns.find((c) => String(c.id) === campaignId);
    const items: NewsletterProductItem[] = (campaign?.items || []).map(
      (item) => ({
        id: item.compound_id,
        name: item.name || item.compound_id,
        image: item.image,
        price: item.price_pvp_with_vat,
        currency: item.currency,
        href: "",
      }),
    );
    update(index, { campaign_id: campaignId, items });
  };

  const handleUpload = async (
    index: number,
    field: "src" | "logo_url",
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const blockId = blocksRef.current[index]?.id;
    setUploadError(null);
    try {
      const url = await uploadNewsletterImage(file);
      // Apply against the CURRENT blocks: the user may have edited or
      // reordered while the upload was in flight.
      const current = blocksRef.current;
      const targetIndex = blockId
        ? current.findIndex((block) => block.id === blockId)
        : index;
      if (targetIndex < 0) return;
      onChange(
        current.map((block, i) =>
          i === targetIndex ? { ...block, [field]: url } : block,
        ),
      );
    } catch (e: any) {
      setUploadError(
        e?.response?.data?.error || "Não foi possível carregar a imagem.",
      );
    } finally {
      event.target.value = "";
    }
  };

  const updateSocial = (
    index: number,
    socialIndex: number,
    patch: Partial<NewsletterSocialLink>,
  ) => {
    const social = [...(blocks[index].social || [])];
    social[socialIndex] = { ...social[socialIndex], ...patch };
    update(index, { social });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {ADDABLE.map((type) => (
          <button
            key={type}
            type="button"
            className={buttonSecondary}
            onClick={() =>
              onChange([...blocks, { ...newBlock(type), id: newBlockId() }])
            }
          >
            + {BLOCK_LABELS[type] || type}
          </button>
        ))}
      </div>

      {uploadError && (
        <div
          role="alert"
          className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {uploadError}
        </div>
      )}

      {blocks.length === 0 && (
        <p className="text-sm text-gray-500">
          Adiciona blocos para construir a newsletter.
        </p>
      )}

      {blocks.map((block, index) => (
        <div
          key={block.id || index}
          data-testid="newsletter-block"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            dropAt(index);
          }}
          className={`rounded-lg border bg-gray-50 p-3 ${
            dragIndex === index
              ? "border-blue-400 ring-2 ring-blue-200"
              : "border-gray-200"
          }`}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <span
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData("text/plain", String(index));
                  setDragIndex(index);
                }}
                onDragEnd={() => setDragIndex(null)}
                className="cursor-grab"
                aria-label="Arrastar para reordenar"
                title="Arrastar para reordenar"
              >
                <GripVertical
                  size={14}
                  className="text-gray-400"
                  aria-hidden="true"
                />
              </span>
              {BLOCK_LABELS[block.type] || block.type}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                className="rounded p-1 text-gray-500 hover:bg-white disabled:opacity-30"
                onClick={() => move(index, -1)}
                aria-label="Mover para cima"
                disabled={index === 0}
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                className="rounded p-1 text-gray-500 hover:bg-white disabled:opacity-30"
                onClick={() => move(index, 1)}
                aria-label="Mover para baixo"
                disabled={index === blocks.length - 1}
              >
                <ArrowDown size={14} />
              </button>
              <button
                type="button"
                className="rounded p-1 text-red-600 hover:bg-white"
                onClick={() => remove(index)}
                aria-label="Remover bloco"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {(block.type === "heading" || block.type === "text") && (
            <>
              <textarea
                className={inputClass}
                rows={block.type === "heading" ? 1 : 4}
                value={
                  block.type === "heading" ? block.text || "" : block.html || ""
                }
                onChange={(e) =>
                  update(
                    index,
                    block.type === "heading"
                      ? { text: e.target.value }
                      : { html: e.target.value },
                  )
                }
              />
              <div className="mt-2">
                <StyleFields
                  block={block}
                  onChange={(patch) => update(index, patch)}
                />
              </div>
            </>
          )}

          {block.type === "html" && (
            <textarea
              className={`${inputClass} font-mono`}
              rows={6}
              aria-label="HTML"
              value={block.html || ""}
              onChange={(e) => update(index, { html: e.target.value })}
            />
          )}

          {block.type === "image" && (
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                className={inputClass}
                placeholder="URL da imagem"
                aria-label="URL da imagem"
                value={block.src || ""}
                onChange={(e) => update(index, { src: e.target.value })}
              />
              <input
                className={inputClass}
                placeholder="Texto alternativo"
                aria-label="Texto alternativo"
                value={block.alt || ""}
                onChange={(e) => update(index, { alt: e.target.value })}
              />
              <input
                className={inputClass}
                placeholder="Link (opcional)"
                aria-label="Link (opcional)"
                value={block.href || ""}
                onChange={(e) => update(index, { href: e.target.value })}
              />
              <label className={buttonSecondary}>
                <Upload size={14} /> Carregar imagem
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml,.svg"
                  className="sr-only"
                  aria-label="Carregar imagem"
                  onChange={(e) => handleUpload(index, "src", e)}
                />
              </label>
            </div>
          )}

          {block.type === "image-text" && (
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  className={inputClass}
                  placeholder="URL da imagem"
                  aria-label="URL da imagem"
                  value={block.src || ""}
                  onChange={(e) => update(index, { src: e.target.value })}
                />
                <input
                  className={inputClass}
                  placeholder="Texto alternativo"
                  aria-label="Texto alternativo"
                  value={block.alt || ""}
                  onChange={(e) => update(index, { alt: e.target.value })}
                />
                <select
                  className={inputClass}
                  value={block.image_position || "left"}
                  onChange={(e) =>
                    update(index, {
                      image_position: e.target.value as NewsletterImagePosition,
                    })
                  }
                  aria-label="Posição da imagem"
                >
                  <option value="left">Imagem à esquerda</option>
                  <option value="right">Imagem à direita</option>
                </select>
                <label className={buttonSecondary}>
                  <Upload size={14} /> Carregar imagem
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/svg+xml,.svg"
                    className="sr-only"
                    aria-label="Carregar imagem"
                    onChange={(e) => handleUpload(index, "src", e)}
                  />
                </label>
              </div>
              <input
                className={inputClass}
                placeholder="Título"
                aria-label="Título"
                value={block.title || ""}
                onChange={(e) => update(index, { title: e.target.value })}
              />
              <textarea
                className={inputClass}
                rows={3}
                placeholder="Texto"
                aria-label="Texto"
                value={block.html || ""}
                onChange={(e) => update(index, { html: e.target.value })}
              />
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  className={inputClass}
                  placeholder="Texto do botão"
                  aria-label="Texto do botão"
                  value={block.label || ""}
                  onChange={(e) => update(index, { label: e.target.value })}
                />
                <input
                  className={inputClass}
                  placeholder="Link do botão"
                  aria-label="Link do botão"
                  value={block.href || ""}
                  onChange={(e) => update(index, { href: e.target.value })}
                />
              </div>
              <StyleFields
                block={block}
                onChange={(patch) => update(index, patch)}
              />
            </div>
          )}

          {block.type === "button" && (
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  className={inputClass}
                  placeholder="Texto do botão"
                  aria-label="Texto do botão"
                  value={block.label || ""}
                  onChange={(e) => update(index, { label: e.target.value })}
                />
                <input
                  className={inputClass}
                  placeholder="Link"
                  aria-label="Link"
                  value={block.href || ""}
                  onChange={(e) => update(index, { href: e.target.value })}
                />
              </div>
              <StyleFields
                block={block}
                onChange={(patch) => update(index, patch)}
              />
            </div>
          )}

          {block.type === "spacer" && (
            <input
              className={inputClass}
              type="number"
              min={4}
              max={120}
              value={block.height || 24}
              onChange={(e) =>
                update(index, { height: Number(e.target.value) || 24 })
              }
            />
          )}

          {(block.type === "products" || block.type === "campaign") && (
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <select
                  className={inputClass}
                  aria-label="Layout dos produtos"
                  value={block.layout || "grid2"}
                  onChange={(e) => update(index, { layout: e.target.value })}
                >
                  <option value="grid1">1 coluna</option>
                  <option value="grid2">2 colunas</option>
                  <option value="grid3">3 colunas</option>
                </select>
                <select
                  className={inputClass}
                  aria-label="Campanha de produtos"
                  value={block.campaign_id || ""}
                  onChange={(e) => importCampaignItems(index, e.target.value)}
                >
                  <option value="">Selecionar campanha…</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              {block.type === "campaign" && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    className={inputClass}
                    placeholder="Título"
                    aria-label="Título"
                    value={block.title || ""}
                    onChange={(e) => update(index, { title: e.target.value })}
                  />
                  <input
                    className={inputClass}
                    placeholder="Subtítulo"
                    aria-label="Subtítulo"
                    value={block.subtitle || ""}
                    onChange={(e) =>
                      update(index, { subtitle: e.target.value })
                    }
                  />
                </div>
              )}
              <p className="text-xs text-gray-500">
                {(block.items || []).length} produto(s). Numa campanha, os
                produtos e preços são atualizados no momento do envio.
              </p>
              {(block.items || []).map((item, itemIndex) => (
                <div
                  key={item.id || itemIndex}
                  className="grid gap-2 sm:grid-cols-3"
                >
                  <input
                    className={inputClass}
                    aria-label="Nome do produto"
                    value={item.name}
                    onChange={(e) => {
                      const items = [...(block.items || [])];
                      items[itemIndex] = {
                        ...items[itemIndex],
                        name: e.target.value,
                      };
                      update(index, { items });
                    }}
                  />
                  <input
                    className={inputClass}
                    type="number"
                    step="0.01"
                    aria-label="Preço do produto"
                    value={item.price ?? 0}
                    onChange={(e) => {
                      const items = [...(block.items || [])];
                      items[itemIndex] = {
                        ...items[itemIndex],
                        price: Number(e.target.value) || 0,
                      };
                      update(index, { items });
                    }}
                  />
                  <input
                    className={inputClass}
                    aria-label="Link do produto"
                    value={item.href || ""}
                    onChange={(e) => {
                      const items = [...(block.items || [])];
                      items[itemIndex] = {
                        ...items[itemIndex],
                        href: e.target.value,
                      };
                      update(index, { items });
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {block.type === "coupon" && (
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  className={inputClass}
                  placeholder="Código do cupão"
                  aria-label="Código do cupão"
                  value={block.coupon_code || ""}
                  onChange={(e) =>
                    update(index, { coupon_code: e.target.value })
                  }
                />
                <input
                  className={inputClass}
                  placeholder="Validade (ex.: 31/12)"
                  aria-label="Validade (ex.: 31/12)"
                  value={block.coupon_expires || ""}
                  onChange={(e) =>
                    update(index, { coupon_expires: e.target.value })
                  }
                />
              </div>
              <input
                className={inputClass}
                placeholder="Título (opcional)"
                aria-label="Título (opcional)"
                value={block.title || ""}
                onChange={(e) => update(index, { title: e.target.value })}
              />
              <input
                className={inputClass}
                placeholder="Descrição (opcional)"
                aria-label="Descrição (opcional)"
                value={block.coupon_description || ""}
                onChange={(e) =>
                  update(index, { coupon_description: e.target.value })
                }
              />
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  className={inputClass}
                  placeholder="Texto do botão"
                  aria-label="Texto do botão"
                  value={block.coupon_cta_label || ""}
                  onChange={(e) =>
                    update(index, { coupon_cta_label: e.target.value })
                  }
                />
                <input
                  className={inputClass}
                  placeholder="Link do botão"
                  aria-label="Link do botão"
                  value={block.coupon_cta_href || ""}
                  onChange={(e) =>
                    update(index, { coupon_cta_href: e.target.value })
                  }
                />
              </div>
              <StyleFields
                block={block}
                onChange={(patch) => update(index, patch)}
              />
            </div>
          )}

          {block.type === "social" && (
            <div className="space-y-2">
              {(block.social || []).map((social, socialIndex) => (
                <div
                  key={socialIndex}
                  className="flex flex-wrap items-end gap-2"
                >
                  <select
                    className={smallInputClass}
                    value={social.network}
                    onChange={(e) =>
                      updateSocial(index, socialIndex, {
                        network: e.target.value,
                      })
                    }
                    aria-label="Rede social"
                  >
                    {SOCIAL_NETWORKS.map((network) => (
                      <option key={network} value={network}>
                        {network}
                      </option>
                    ))}
                  </select>
                  <input
                    className={smallInputClass}
                    placeholder="https://..."
                    value={social.url}
                    onChange={(e) =>
                      updateSocial(index, socialIndex, { url: e.target.value })
                    }
                    aria-label="URL da rede social"
                  />
                  <button
                    type="button"
                    className="rounded p-1 text-red-600 hover:bg-white"
                    onClick={() => {
                      const social = (block.social || []).filter(
                        (_, i) => i !== socialIndex,
                      );
                      update(index, { social });
                    }}
                    aria-label="Remover rede social"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={buttonSecondary}
                onClick={() =>
                  update(index, {
                    social: [
                      ...(block.social || []),
                      { network: "instagram", url: "" },
                    ],
                  })
                }
              >
                <Plus size={14} /> Adicionar rede
              </button>
            </div>
          )}

          {(block.type === "header" || block.type === "footer") && (
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  className={inputClass}
                  placeholder="Nome da loja"
                  aria-label="Nome da loja"
                  value={block.store_name || ""}
                  onChange={(e) =>
                    update(index, { store_name: e.target.value })
                  }
                />
                {block.type === "header" ? (
                  <>
                    <input
                      className={inputClass}
                      placeholder="URL do logótipo"
                      aria-label="URL do logótipo"
                      value={block.logo_url || ""}
                      onChange={(e) =>
                        update(index, { logo_url: e.target.value })
                      }
                    />
                    <label className={buttonSecondary}>
                      <Upload size={14} /> Carregar logótipo
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/svg+xml,.svg"
                        className="sr-only"
                        aria-label="Carregar logótipo"
                        onChange={(e) => handleUpload(index, "logo_url", e)}
                      />
                    </label>
                    {block.logo_url && (
                      <img
                        src={block.logo_url}
                        alt="Pré-visualização do logótipo"
                        className="max-h-12 max-w-[160px] object-contain"
                      />
                    )}
                  </>
                ) : (
                  <input
                    className={inputClass}
                    placeholder="Endereço"
                    aria-label="Endereço"
                    value={block.address || ""}
                    onChange={(e) => update(index, { address: e.target.value })}
                  />
                )}
              </div>
              <StyleFields
                block={block}
                onChange={(patch) => update(index, patch)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default NewsletterBlockEditor;
